import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, FolderOpen, ChevronLeft, ChevronRight, RotateCcw, ArrowUp, ArrowDown } from "lucide-react";
import {
  useAdminProjectsQuery,
  useAdminStatsQuery,
  useAdminRestoreExpiredProjectMutation,
  useAdminRetryFailedProjectMutation,
} from "@/generated/graphql";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import { formatDateTime, formatDuration } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

type SortField = "name" | "curation_status" | "duration_seconds" | "language" | "createdAt";

const PROJECT_STATUSES = ["pending", "processing", "completed", "failure", "expired"];

const AdminProjects = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const pageSize = 20;

  const { data, loading, error, refetch } = useAdminProjectsQuery({
    variables: {
      page,
      pageSize,
      search: search || undefined,
      curation_status: statusFilter || undefined,
      sortBy,
      sortOrder,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: statsData } = useAdminStatsQuery();

  const [restoreExpiredProject] = useAdminRestoreExpiredProjectMutation();
  const [retryFailedProject] = useAdminRetryFailedProjectMutation();

  const chartConfig = {
    total: { label: t("admin.common.chartTotal"), color: "hsl(var(--primary))" },
    success: { label: t("admin.common.chartSuccess"), color: "hsl(142 76% 36%)" },
    failure: { label: t("admin.common.chartFailure"), color: "hsl(0 84% 60%)" },
  } satisfies ChartConfig;

  const timeSeries = statsData?.adminStats?.projects?.timeSeries || [];

  const projects = data?.adminProjects?.data || [];
  const totalPages = data?.adminProjects?.pageCount || 1;
  const total = data?.adminProjects?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };

  const handleRestoreProject = async (projectId: string) => {
    try {
      await restoreExpiredProject({ variables: { id: projectId } });
      toast({ title: t("admin.userDetail.projectRestored") });
      refetch();
    } catch {
      toast({
        title: t("admin.userDetail.restoreError"),
        variant: "destructive",
      });
    }
  };

  const handleRetryProject = async (projectId: string) => {
    try {
      await retryFailedProject({ variables: { id: projectId } });
      toast({ title: t("admin.userDetail.projectRetried") });
      refetch();
    } catch {
      toast({
        title: t("admin.userDetail.retryError"),
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "completed":
      case "success":
        return "bg-green-500/10 text-green-500 border-green-500";
      case "processing":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500";
      case "failed":
      case "failure":
        return "bg-red-500/10 text-red-500 border-red-500";
      case "pending":
        return "bg-blue-500/10 text-blue-500 border-blue-500";
      case "expired":
        return "bg-orange-500/10 text-orange-500 border-orange-500";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500";
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FolderOpen className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t('admin.projects.title')}</h1>
                <p className="text-muted-foreground">{t('admin.projects.subtitle')}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">
              ADMIN
            </Badge>
          </div>

          {timeSeries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.projects.dailyChart')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={timeSeries}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="total" fill="var(--color-total)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="success" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failure" fill="var(--color-failure)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.projects.list')}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t('admin.common.total')}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2 mb-4">
                <Input
                  placeholder={t('admin.projects.searchPlaceholder')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="max-w-sm"
                />
                <Select
                  value={statusFilter || "all"}
                  onValueChange={(value) => {
                    setStatusFilter(value === "all" ? "" : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('admin.projects.filterByStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.projects.allStatuses')}</SelectItem>
                    {PROJECT_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  {t('admin.common.error')}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("name")}>
                            {t('admin.projects.name')}<SortIcon field="name" />
                          </TableHead>
                          <TableHead>{t('admin.projects.user')}</TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("curation_status")}>
                            {t('admin.projects.status')}<SortIcon field="curation_status" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("duration_seconds")}>
                            {t('admin.projects.duration')}<SortIcon field="duration_seconds" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("language")}>
                            {t('admin.projects.language')}<SortIcon field="language" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("createdAt")}>
                            {t('admin.projects.createdAt')}<SortIcon field="createdAt" />
                          </TableHead>
                          <TableHead>{t('admin.users.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {projects.map((project) => (
                          <TableRow key={project.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {project.name || "-"}
                                {project.deleted && (
                                  <Badge variant="outline" className="text-red-500 border-red-500 text-xs">
                                    Deleted
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{project.created_by_user?.name || "-"}</div>
                                <div className="text-muted-foreground text-xs">
                                  {project.created_by_user?.email}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusColor(project.curation_status)}>
                                {project.curation_status || "pending"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {project.duration_seconds
                                ? formatDuration(project.duration_seconds)
                                : "-"}
                            </TableCell>
                            <TableCell>{project.language || "-"}</TableCell>
                            <TableCell>{formatDateTime(project.createdAt)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {project.curation_status === "expired" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRestoreProject(project.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.restore")}
                                  </Button>
                                )}
                                {project.curation_status === "failure" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRetryProject(project.id)}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    {t("admin.userDetail.retry")}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {projects.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                              {t('admin.common.noResults')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
};

export default AdminProjects;
